import { css } from "@emotion/react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { storefrontSdk } from "@/storefrontsdk";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Progress } from "./ui/progress";
//import { useThemeState } from "../hooks/useThemeState";

//----------------------------------------------------------------------------------------------
export type StorefrontProgress = {
	Failed: boolean;
	FailureMessage: string;
	FailureStackTrace: string;
	SessionId: string;
	Caption: string;
	CurrentStep?: number;
	TotalSteps?: number;
	PercentComplete?: number;
};
//----------------------------------------------------------------------------------------------
export type FwProgressMeterProps = {
	sessionId: string;
	open: boolean;
	initialCaption: string;
	dialogTitle: string;
	onComplete?: (e: StorefrontProgress) => void;
	onFailed?: (e: StorefrontProgress) => void;
	onClose?: () => void;
};
export const ProgressMeter: React.FC<FwProgressMeterProps> = (props) => {
	const visible = !!props.sessionId;
	const display = visible ? "block" : "none";
	const translate = useTranslate();

	const initialProgress = useMemo<StorefrontProgress>(
		() => ({
			Failed: false,
			FailureMessage: "",
			FailureStackTrace: "",
			SessionId: props.sessionId,
			Caption: "",
			CurrentStep: 0,
			TotalSteps: 0,
			PercentComplete: 0,
		}),
		[props.sessionId],
	);
	const [progress, setProgress] = useState<StorefrontProgress>(initialProgress);
	const progressRef = useRef(progress);
	useEffect(() => {
		setProgress(initialProgress);
	}, [initialProgress]);
	useEffect(() => {
		progressRef.current = progress;
	}, [progress]);
	const failed = progress.Failed;
	const failureMessage = progress.FailureMessage;
	const failureStackTrace = progress.FailureStackTrace;
	//let currentStep = progress.CurrentStep ?? 0;
	//let totalSteps = progress.TotalSteps ?? 0;
	const caption = progress.Caption ?? "";
	const percentage = progress.PercentComplete ?? 0;
	//let completed = percentage === 100;
	const percentText = `${percentage}%`;
	// dialog
	//const [open, setOpen] = React.useState(visible);
	//const themeState = useThemeState();
	//const theme = themeState.getTheme();
	//const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

	const reset = () => {
		setProgress({
			Failed: false,
			FailureMessage: "",
			FailureStackTrace: "",
			SessionId: "",
			Caption: "",
			CurrentStep: 0,
			TotalSteps: 0,
			PercentComplete: 0,
		});
	};

	const handleClose = () => {
		//setOpen(false);
		reset();
		if (props.onClose) {
			props.onClose();
		}
	};

	// effect to call the webapi to update the progress meter every 3 seconds
	useEffect(() => {
		const interval = setInterval(async () => {
			//console.log('progress interval');
			// don't use progress data from outer scope, since this isn't running on rerender, it's in a timer
			const currentProgress = progressRef.current;
			const percentComplete = currentProgress.PercentComplete ?? 0;
			const isCompleted = percentComplete === 100;
			const isFailed = currentProgress.Failed;
			if (isCompleted || isFailed) {
				try {
					if (isCompleted && props.onComplete) {
						props.onComplete(currentProgress);
					}
					if (isFailed && props.onFailed) {
						props.onFailed(currentProgress);
					}
				} finally {
					clearInterval(interval);
				}
				return;
			}
			if (!props.sessionId) {
				clearInterval(interval);
				return;
			}
			const responseProgressMeter =
				await storefrontSdk.storefrontV1GetProgressMeterAsync(props.sessionId);
			if (responseProgressMeter.status !== 200) {
				//console.error(`Unable to load: ${progressMeterUrl}`, responseProgressMeter.data)
				return;
			}
			setProgress(responseProgressMeter.data as StorefrontProgress);
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [props.sessionId, props.onComplete, props.onFailed]);

	return (
		<div className="progressmeter">
			<Dialog open={props.open}>
				<DialogContent
					showCloseButton={false}
					aria-labelledby="responsive-dialog-title"
				>
					<DialogTitle id="responsive-dialog-title">
						{props.dialogTitle}
					</DialogTitle>
					<div css={css`width:350px;`} style={{ display: display }}>
						<div
							css={css`padding:.5rem 0;font-size:.8rem;`}
							style={{ textDecoration: failed ? "line-through" : "auto" }}
						>
							{caption}
						</div>
						<Progress value={progress.PercentComplete ?? 0} />
						<div
							css={css`padding: 0 0 1rem 0;text-align:right;`}
							style={{ textDecoration: failed ? "line-through" : "auto" }}
						>
							{percentText}
						</div>
						{failed && (
							<>
								<div css={css`color:red;font-size:.8rem;padding: .5rem;`}>
									Error:
									<br />
									{failureMessage}
								</div>
								<div css={css`margin:1rem 0 0 0;font-size:.7rem;display:none;`}>
									Stack Trace:
									<br />
									{failureStackTrace}
								</div>
							</>
						)}
					</div>
					{(failed || progress.PercentComplete === 100) && (
						<div className="flex justify-end pt-2">
							<Button onClick={handleClose} autoFocus variant="outline">
								{translate("Close")}
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};
//----------------------------------------------------------------------------------------------
